import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../core/redux/store";
import { statsService } from "../core/services/stats.service";
import { utils } from "../core/helpers";
import { Table } from "antd";
import ImageWithBasePath from "../core/common/imageWithBasePath";
import Breadcrumb from "../core/common/Breadcrumb/breadcrumb";
import { fetchClassroomById } from "../core/redux/classroomSlice";
import {useReportsLogic} from "../core/helpers/useReportsLogic";
import {Spinner} from "react-bootstrap";
import SkillBarChart from "./home/components/SkillBarChart";
import SkillStatisticsTable from "./home/components/SkillStatisticsTable";
import ProfileCard from "../feature-module/Instructor/common/profileCard";

const Student: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { studentId, classCode } = useParams();
  const { user } = useSelector((state: RootState) => state.user);
  const { current } = useSelector((state: RootState) => state.classrooms);
  const [latestContents, setLatestContents] = useState<any[]>([]);
  const [isLatestContentsFetched, setIsLatestContentsFetched] = useState(false);
  const [student, setStudent] = useState<any>(null);


  useEffect(() => {
    if (!(!!utils.getPasswordToken() && !!user)) {
      navigate("/login");
      return;
    }
  }, []);

  useEffect(() => {
    if (!classCode) return;
    const id = Number(classCode);
    if (!Number.isFinite(id)) return;
    if (!current || current.id !== id) {
      dispatch(fetchClassroomById({ id }));
    }
  }, [classCode, dispatch, current]);

  useEffect(() => {
    if (studentId && current) {
      setStudent(current.users.find((user: any) => user.id == studentId));
    }
  }, [studentId, current]);

  useEffect(() => {
    if (studentId && latestContents.length === 0 && !isLatestContentsFetched) {
      statsService.getStudentsLatestContents(studentId).then((data) => {
        // get only 20 of data
        setLatestContents(data.slice(0, 20));
        setIsLatestContentsFetched(true);
      });
    }
  }, [latestContents, dispatch, isLatestContentsFetched, studentId]);

    const {
        chartData,
        isLoading,
        currentChartIndex,
        nextChart,
        prevChart,
        setCurrentChartIndex
    } = useReportsLogic(studentId);

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        );
    }

    const renderStudentCharts = (
        chartData.length > 0 &&
        <div className="container my-5">
            <div className="row justify-content-center">
                <div className="col-12 col-lg-12">
                    <div className="card-body">
                        <div
                            className="chart-carousel d-flex d-sm-flex flex-column-reverse flex-sm-row align-items-center justify-content-between">
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm mt-3 mt-sm-0 me-sm-2"
                                onClick={prevChart}
                                aria-label="Previous Chart"
                            >
                                <i className="fa fa-chevron-left" />
                            </button>
                            <div style={{ flexGrow: 1, minHeight: '300px', maxHeight: '550px', width: '100%', maxWidth: '100%' }}>
                                {chartData[currentChartIndex] && <SkillBarChart skillData={chartData[currentChartIndex]} />}
                            </div>
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm mb-3 mb-sm-0 ms-sm-2"
                                onClick={nextChart}
                                aria-label="Next Chart"
                            >
                                <i className="fa fa-chevron-right" />
                            </button>
                        </div>
                        <div className="d-flex justify-content-center mt-3">
                            {chartData.map((_, index) => (
                                <span
                                    key={index}
                                    className={`mx-1 d-inline-block rounded-circle ${index === currentChartIndex ? 'bg-primary' : 'bg-secondary'}`}
                                    style={{ width: '8px', height: '8px', cursor: 'pointer' }}
                                    onClick={() => setCurrentChartIndex(index)}
                                    aria-label={`Go to chart ${index + 1}`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <Breadcrumb title={`Report of ${student?.name}`} subtitles={[
                {
                    title: "Cohorts",
                    link: "/student-groups/"
                },
                {
                    title: current?.name,
                    link: `/student-groups/${classCode}/students`
                }
            ]}/>
            <div className="content pb-0">
                <div className="container">
                    <button
                        className="btn btn-dark rounded-pill"
                        type="button"
                        onClick={() => {
                            if (typeof window !== "undefined") {
                                window.history.back()
                            }
                        }}
                    >
                        <i className="isax isax-arrow-left-2 me-2"></i>
                        Back
                    </button>
                </div>
            </div>
            {renderStudentCharts}
            {chartData.length > 0 && (
                <SkillStatisticsTable chartData={chartData} studentId={studentId} />
            )}
            {chartData.length === 0 && !isLoading && (
                <div className="container my-5">
                    <div className="section-header text-center topcategory-sec">
                        <h2>No Data Available</h2>
                        <p>Complete some activities to see your progress reports.</p>
                    </div>
                </div>
            )}
        </>
    );
};

export default Student;
